<template>
  <transition name="confirm-modal-fade">
    <div v-if="visible" class="confirm-modal-backdrop" @click.self="close">
      <section
        class="confirm-modal"
        role="alertdialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        :aria-describedby="messageId"
      >
        <header class="confirm-modal-header">
          <div
            class="confirm-modal-icon"
            :class="`confirm-modal-icon--${tone}`"
            aria-hidden="true"
          >
            <i :class="iconClass"></i>
          </div>
          <div>
            <h3 :id="titleId">{{ title }}</h3>
            <p v-if="eyebrow" class="confirm-modal-eyebrow">{{ eyebrow }}</p>
          </div>
          <button
            class="confirm-modal-close"
            type="button"
            title="Chiudi"
            @click="close"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
        </header>

        <p :id="messageId" class="confirm-modal-message">
          {{ message }}
        </p>

        <div class="confirm-modal-actions">
          <button
            class="confirm-modal-button confirm-modal-button--secondary"
            type="button"
            @click="close"
          >
            {{ cancelLabel }}
          </button>
          <button
            class="confirm-modal-button"
            :class="`confirm-modal-button--${tone}`"
            type="button"
            @click="confirm"
          >
            <i :class="confirmIconClass"></i>
            {{ confirmLabel }}
          </button>
        </div>
      </section>
    </div>
  </transition>
</template>

<script>
export default {
  name: "ConfirmActionModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Conferma operazione",
    },
    eyebrow: {
      type: String,
      default: "",
    },
    message: {
      type: String,
      default: "",
    },
    confirmLabel: {
      type: String,
      default: "Conferma",
    },
    cancelLabel: {
      type: String,
      default: "Annulla",
    },
    iconClass: {
      type: String,
      default: "fa-solid fa-triangle-exclamation",
    },
    confirmIconClass: {
      type: String,
      default: "fa-solid fa-check",
    },
    tone: {
      type: String,
      default: "danger",
    },
  },
  data() {
    const uid = this._uid || Math.random().toString(36).slice(2);
    return {
      titleId: `confirm-modal-title-${uid}`,
      messageId: `confirm-modal-message-${uid}`,
    };
  },
  watch: {
    visible(isVisible) {
      if (isVisible) {
        document.addEventListener("keydown", this.handleKeydown);
      } else {
        document.removeEventListener("keydown", this.handleKeydown);
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.handleKeydown);
  },
  methods: {
    close() {
      this.$emit("close");
    },
    confirm() {
      this.$emit("confirm");
    },
    handleKeydown(event) {
      if (event.key === "Escape") {
        this.close();
      }
    },
  },
};
</script>

<style scoped>
.confirm-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(18, 24, 32, 0.58);
}

.confirm-modal {
  width: min(32rem, 100%);
  overflow: hidden;
  background: #fff;
  border: 1px solid #d9dde3;
  border-radius: 8px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28);
  color: #252a31;
}

.confirm-modal-header {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.9rem;
  align-items: center;
  padding: 1.15rem 1.25rem;
  background: #f5f6f8;
  border-bottom: 1px solid #e2e5e9;
}

.confirm-modal-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.45rem;
  height: 2.45rem;
  color: #9f2f2f;
  background: #fff1f1;
  border: 1px solid #f3c7c7;
  border-radius: 50%;
}

.confirm-modal-icon--info {
  color: #1f6fb2;
  background: #eef7ff;
  border-color: #bfdbfe;
}

.confirm-modal-icon i {
  font-size: 1.1rem;
}

.confirm-modal h3 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.25;
}

.confirm-modal-eyebrow {
  margin: 0.2rem 0 0;
  color: #6a717d;
  font-size: 0.82rem;
  font-weight: 600;
  text-transform: uppercase;
}

.confirm-modal-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  color: #69707b;
  background: transparent;
  border: 0;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.confirm-modal-close:hover {
  color: #252a31;
  background: #e8eaee;
}

.confirm-modal-message {
  margin: 0;
  padding: 1.35rem 1.5rem;
  color: #3f4651;
  font-size: 1rem;
  line-height: 1.5;
  white-space: pre-wrap;
}

.confirm-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: #fafafa;
  border-top: 1px solid #e2e5e9;
}

.confirm-modal-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-width: 8.5rem;
  min-height: 2.45rem;
  padding: 0.65rem 1rem;
  border: 1px solid transparent;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.confirm-modal-button--secondary {
  color: #333943;
  background: #fff;
  border-color: #cbd1d8;
}

.confirm-modal-button--secondary:hover {
  background: #f0f2f4;
  border-color: #b8c0c9;
}

.confirm-modal-button--danger {
  color: #fff;
  background: #b33a3a;
  border-color: #a73535;
}

.confirm-modal-button--danger:hover {
  background: #9c3030;
  border-color: #8f2c2c;
}

.confirm-modal-button--info {
  color: #ffffff;
  background: #1f8fe5;
  border-color: #177ccc;
}

.confirm-modal-button--info:hover {
  background: #177ccc;
  border-color: #126daf;
}

.confirm-modal-fade-enter-active,
.confirm-modal-fade-leave-active {
  transition: opacity 0.18s ease;
}

.confirm-modal-fade-enter,
.confirm-modal-fade-leave-to {
  opacity: 0;
}

@media (max-width: 520px) {
  .confirm-modal-header {
    grid-template-columns: auto 1fr auto;
    padding: 1rem;
  }

  .confirm-modal-actions {
    flex-direction: column-reverse;
  }

  .confirm-modal-button {
    width: 100%;
  }
}
</style>
